/**
 * Session handling for the custom `/login` page.
 *
 * A Clerk client can already hold a session when `/login` renders: an active
 * one (the visitor typed `/login` or followed a stale link), or a `pending` one
 * that still owes a session task — for example `reset-password`, which Clerk
 * raises after sign-in when the password appears in a breach corpus
 * (`enforce_hibp_on_sign_in` is on in production). `useAuth()` reports a
 * pending session as signed out, so `RequireAuth` sends it back here, and every
 * new sign-in attempt then fails with `session_exists` ("You're already signed
 * in."). The page has to finish or forward that session instead of showing the
 * sign-in form again.
 */

export type LoginSessionTaskKey = 'reset-password' | 'setup-mfa' | 'choose-organization';

type SessionLike =
  | {
      status: string;
      currentTask?: { key: string } | null;
    }
  | null
  | undefined;

export type LoginSessionState =
  | { kind: 'none' }
  | { kind: 'active' }
  | { kind: 'task'; taskKey: string };

const TASK_KEYS: ReadonlySet<string> = new Set<LoginSessionTaskKey>([
  'reset-password',
  'setup-mfa',
  'choose-organization',
]);

export function isLoginSessionTaskKey(value: string): value is LoginSessionTaskKey {
  return TASK_KEYS.has(value);
}

export function resolveLoginSessionState(session: SessionLike): LoginSessionState {
  if (!session) return { kind: 'none' };
  if (session.status === 'pending' && session.currentTask) {
    return { kind: 'task', taskKey: session.currentTask.key };
  }
  if (session.status === 'active') return { kind: 'active' };
  return { kind: 'none' };
}

/** `/login/tasks/<key>`, carrying the post-auth destination through `next`. */
export function loginTaskPath(taskKey: string, destination?: string): string {
  const query = destination ? `?next=${encodeURIComponent(destination)}` : '';
  return `/login/tasks/${encodeURIComponent(taskKey)}${query}`;
}

import { hasClerkErrorCode } from '@/lib/auth/clerk-error-code';

/** Clerk's "You're already signed in." rejection of a new sign-in attempt. */
export function isSessionExistsError(error: unknown): boolean {
  return hasClerkErrorCode(error, ['session_exists']);
}

/**
 * Clerk's refusal of a correct password that it will no longer accept:
 * `form_password_pwned` (found in a breach corpus; production has
 * `enforce_hibp_on_sign_in`) or `form_password_compromised` (marked
 * compromised by an admin). Either way the way back in is a password reset.
 */
export function isPasswordBreachedError(error: unknown): boolean {
  return hasClerkErrorCode(error, ['form_password_pwned', 'form_password_compromised']);
}

/** No account exists for the email the visitor entered on the combined auth screen. */
export function isIdentifierNotFoundError(error: unknown): boolean {
  return hasClerkErrorCode(error, ['form_identifier_not_found']);
}

/**
 * Password sign-in is unavailable for this account or instance (no password set,
 * password auth disabled in Clerk, or strategy not allowed).
 */
export function isPasswordAuthUnavailableError(error: unknown): boolean {
  return hasClerkErrorCode(error, [
    'form_password_not_set',
    'strategy_for_user_not_allowed',
    'not_allowed_access',
    'form_param_format_invalid',
  ]);
}
